import { IconButton, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useGetAllNodesByDeploymentArrayActionMutation } from '@common-reducers/backend-api/backend-k8s-api';
import { Node } from '@website-views/k8s-analyzer/k8s-analyzer-types';

export interface UseNodesFetchResult {
    nodes: Node[];
    isLoading: boolean;
    isRefreshing: boolean;
    connectionError: string | null;
    retryCount: number;
    retry: () => Promise<void>;
}

export function useNodesFetch(): UseNodesFetchResult {
    const [getAllNodes] = useGetAllNodesByDeploymentArrayActionMutation();
    const [nodes, setNodes] = useState<Node[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const fetchNodes = useCallback(async () => {
        setIsRefreshing(true);
        setConnectionError(null);
        try {
            const result = await getAllNodes(['gateway']).unwrap();
            const nodesData = Array.isArray(result) ? result : result?.nodes;
            if (nodesData && nodesData.length > 0) {
                const formattedNodes = nodesData.map((node: any) => ({
                    ...node,
                    pods_list: node.pods || [],
                    role: node.role || '<none>',
                    taints: node.taints || 0,
                    version: node.version || 'v1.xx.x',
                    pods: node.pods?.length || 0,
                    age: node.age || '?d',
                }));
                
                setNodes(formattedNodes);
                setRetryCount(0);
            } else {
                console.warn('No nodes in response:', result);
                setNodes([]);
                setConnectionError('Received empty response from server');
            }
        } catch (error: any) {
            console.error('Failed to fetch nodes:', error);
            setNodes([]);
            
            if (error.status === 'FETCH_ERROR') {
                setConnectionError('Connection to server failed. Backend service may be unavailable.');
            } else if (error.status >= 500) {
                setConnectionError(`Server error: ${error.data?.message || 'Unknown server error'}`);
            } else if (error.status >= 400) {
                setConnectionError(`Request error: ${error.data?.message || 'Invalid request'}`);
            } else {
                setConnectionError(`Error: ${error.message || 'Unknown error occurred'}`);
            }
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [getAllNodes]);

    const retry = useCallback(async () => {
        if (retryCount < 3) {
            setRetryCount(prev => prev + 1);
            await fetchNodes();
        }
    }, [fetchNodes, retryCount]);

    useEffect(() => {
        if (connectionError && retryCount < 3) {
            const timer = setTimeout(() => {
                retry();
            }, 5000);
            
            return () => clearTimeout(timer);
        }
    }, [connectionError, retryCount, retry]);

    useEffect(() => {
        fetchNodes();
        const interval = setInterval(fetchNodes, 30000);
        return () => clearInterval(interval);
    }, [fetchNodes]);

    return {
        nodes,
        isLoading,
        isRefreshing,
        connectionError,
        retryCount,
        retry
    };
}

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

export function K8sAnalyzerNodesListRefreshButton() {
    const { isRefreshing, retry } = useNodesFetch();

    return (
        <Tooltip title="Refresh data">
            <IconButton onClick={() => retry()} disabled={isRefreshing}>
                <Refresh sx={{ animation: isRefreshing ? `${spin} 1s linear infinite` : 'none' }} />
            </IconButton>
        </Tooltip>
    );
} 